import React, { ChangeEvent, useState } from 'react';

import { observer } from 'mobx-react-lite';
import { useDetectClickOutside } from 'react-detect-click-outside';

import TextInput from 'components/common/TextInput/TextInput';
import InputSelect from 'components/common/InputSelect/InputSelect';
import { getStateOptions } from 'components/common/AddressInputs/addressInputs.utils';
import { STATE_ABBREVIATION_MAX_LENGTH, ZIP_CODE_MAX_LENGTH } from 'constants/address.constants';
import { AddressLabels } from 'components/common/AddressInputs/addressInputs.types';
import { Status, useGooglePlace } from 'hooks/useGooglePlace';

import styles from './AddressInputs.module.scss';

interface AddressInputsProps {
  values: Record<string, any>;
  errors: Record<string, any>;
  addressLabels: AddressLabels;
  onChange: (name: string, value: any) => void;
  onInputChange: (value: any) => void;
  onBlur?: (event: any) => void;
  disabled?: boolean;
  useRequiredSymbol?: boolean;
}

const AddressInputs = ({
  values,
  errors,
  addressLabels,
  onChange,
  onInputChange,
  onBlur = () => {},
  disabled = false,
  useRequiredSymbol = false,
}: AddressInputsProps) => {
  const { predictions, getSuggestions, clearSuggestions, status, getPlaceDetails } = useGooglePlace();
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const statesOptions = getStateOptions();

  const ref = useDetectClickOutside({
    onTriggered: () => {
      setIsFocused(false);

      clearSuggestions();
    },
  });

  const handleOnFocus = (): void => {
    setIsFocused(true);
  };

  const handleInput = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    setIsFocused(true);

    await getSuggestions(event.target.value);
  };

  const handleSelect = async (suggestion: google.maps.places.AutocompleteSuggestion) => {
    if (!suggestion?.placePrediction?.placeId) {
      return;
    }

    const parts = await getPlaceDetails(suggestion?.placePrediction?.placeId);

    onInputChange({ streetAddress: parts.streetAddress, city: parts.city, state: parts.state, zip: parts.zip });
    clearSuggestions();
  };

  const renderSuggestions = (): React.ReactNode => {
    return predictions.map((suggestion: google.maps.places.AutocompleteSuggestion, index: number) => {
      const { placePrediction } = suggestion;

      return (
        <li key={index} className={styles.listItem} onClick={() => handleSelect(suggestion)} role="option">
          <span>{placePrediction?.mainText?.text} </span>
          <span>{placePrediction?.secondaryText?.text}</span>
        </li>
      );
    });
  };

  const hasSuggestions = status === Status.Ok;

  return (
    <div ref={ref}>
      <div aria-expanded={hasSuggestions} className={styles.formInput}>
        <div className={styles.streetAddressInput}>
          <TextInput
            className={styles.input}
            containerClassName={styles.inputContainer}
            labelTitle={addressLabels.address}
            name={addressLabels.address}
            value={values[addressLabels.address]}
            errorMessage={errors[addressLabels.address]}
            onChange={(event) => {
              onChange(event.target.name, event.target.value);
              handleInput(event);
            }}
            onBlur={onBlur}
            onFocus={handleOnFocus}
            disabled={disabled}
            useRequiredSymbol={useRequiredSymbol}
          />
          {isFocused && hasSuggestions && <ul className={styles.list}>{renderSuggestions()}</ul>}
        </div>
      </div>
      <div className={styles.formInput}>
        <TextInput
          className={styles.input}
          containerClassName={styles.inputContainer}
          labelTitle={addressLabels.city}
          name={addressLabels.city}
          value={values[addressLabels.city]}
          errorMessage={errors[addressLabels.city]}
          onChange={(event) => onChange(event.target.name, event.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          useRequiredSymbol={useRequiredSymbol}
        />
        <InputSelect
          items={statesOptions}
          disabled={disabled}
          currentValue={statesOptions.find((obj) => obj.value === values[addressLabels.state])?.label}
          onChange={(option: string) => {
            if (
              !option ||
              option.length > STATE_ABBREVIATION_MAX_LENGTH ||
              !statesOptions.find((state) => state.value === option)
            ) {
              return;
            }

            if (onChange) {
              onChange(addressLabels.state, option);
            }
          }}
          label={addressLabels.state}
          errorMessage={errors[addressLabels.state]}
          useRequiredSymbol={useRequiredSymbol}
        />
        <TextInput
          className={styles.input}
          containerClassName={styles.inputContainer}
          labelTitle={addressLabels.zip}
          name={addressLabels.zip}
          value={values[addressLabels.zip]?.trim().replace(/\D/g, '')}
          errorMessage={errors[addressLabels.zip]}
          onChange={(event) => onChange(event.target.name, event.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          useRequiredSymbol={useRequiredSymbol}
          maxLength={ZIP_CODE_MAX_LENGTH}
        />
      </div>
    </div>
  );
};

export default observer(AddressInputs);
